import {
    DistanceUnit,
    HotspotStatus,
    HotspotWithLabel,
    InfraredImageWithContext,
    WorkspaceConfirmationModalName,
} from "@nova/constants"
import {
    selectNonRemovedHotspotImageIdMap,
    selectHotspotWithLabelById,
} from "features/entity-caching/hotspots/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Resizable } from "re-resizable"
import {
    FunctionComponent,
    memo,
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react"
import * as Icons from "@nova/component-icons"
import { ButtonDestructive, ButtonIcon } from "@nova/component-btn"
import {
    selectInfraredImageById,
    selectInfraredImagesContextMap,
} from "features/entity-caching/images/hotspots/selectors"
import { selectInfraredFlightById } from "features/entity-caching/flights/selectors"
import { InfraredFlight } from "@nova/protobuf"
import {
    InfraredImage,
    prePopulateInfraredImageData,
} from "features/data-management/hotspots/editor/infrared-image/InfraredImage"
import { processPolygon } from "./polygonToPerimeter"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { CopyButton } from "@nova/component-copy-button"
import { DateFormat, formatDate, formatDistance } from "@nova/format"
import { Divider } from "components/Divider"
import { HotspotPanelList } from "./HotspotPanelList"
import {
    selectHotspotPanelFilters,
    selectHotspotPanelImages,
    selectHotspotPanelItems,
    selectHotspotPanelListOpen,
} from "./selectors"
import { setWorkspaceConfirmationModalContext } from "features/content-overlay/slice"
import { showGlobalModal } from "runlevel/actions"
import { findNext } from "./findNext"
import {
    closeHotspotPanel,
    setHotspotPanelState,
    toggleFieldOfView,
    toggleImageOverlay,
} from "./slice"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { getInfraredBoundingBox } from "plugins/InfraredImage/useInfraredImageBoundingBox"
import { ReactZoomPanPinchContentRef } from "react-zoom-pan-pinch"
import { useCursorTracking } from "tools/events/useCursorProvider"
import { selectActiveFirePerimeterPolygons } from "features/entity-caching/fire-perimeters/selectors"
import { HotspotInPerimeterBox } from "./HotspotInPerimeterBox"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { isFormFieldFocused } from "@nova/util-browser"

interface HotspotPanelContentProps {
    hotspot: HotspotWithLabel | undefined
    flight: InfraredFlight
    image: InfraredImageWithContext | undefined
}

const prefetchInfraredImages = (
    contextMap: Record<string, InfraredImageWithContext>,
    getNextImageId: (offset: number) => string | undefined,
    depth: number = 3,
    isDemo: boolean
) => {
    for (let i = 1; i < depth; i++) {
        const posImageId = getNextImageId(i) ?? ""
        const negativeImageId = getNextImageId(-i) ?? ""

        const posImage = contextMap[posImageId]
        const negativeImage = contextMap[negativeImageId]

        if (posImage) {
            prePopulateInfraredImageData(posImage.s3Key, isDemo)
        }
        if (negativeImage) {
            prePopulateInfraredImageData(negativeImage.s3Key, isDemo)
        }
    }
}

const HotspotPanelContent: FunctionComponent<HotspotPanelContentProps> = ({
    hotspot,
    flight,
    image,
}) => {
    const [isImageZoomed, setIsImageZoomed] = useState(false)
    const dispatch = useAppDispatch()
    const images = useAppSelector(selectHotspotPanelImages(flight.id))
    const imageMap = useAppSelector(selectNonRemovedHotspotImageIdMap(flight.id))
    const hotspotPanelFilters = useAppSelector(selectHotspotPanelFilters)
    const contextMap = useAppSelector(selectInfraredImagesContextMap)
    const zoomRef = useRef<ReactZoomPanPinchContentRef>(null)

    const getNextImage = useCallback(
        (direction: number = 1) => {
            const currentImageIndex = images.findIndex((i) => i.id === image?.id)

            if (currentImageIndex === -1) {
                return images.at(0)
            }

            const newImage = findNext(
                images.filter(
                    (image) => !hotspotPanelFilters.withHotspots || imageMap.has(image.id)
                ),
                currentImageIndex,
                () => true,
                direction
            )

            return newImage
        },
        [hotspotPanelFilters.withHotspots, image?.id, imageMap, images]
    )

    const incDecImage = (direction: number = -1) => {
        const newImage = getNextImage(direction)

        if (!newImage) return

        dispatch(setHotspotPanelState({ imageId: newImage.id, hotspotId: undefined }))

        getInfraredBoundingBox(newImage, (res) => {
            dispatch(zoomToShowPoints({ points: res as [number, number][], preserveZoom: true }))
        })
    }

    const getNextImageId = (offset: number) => {
        const nextImage = getNextImage(offset)

        return nextImage?.id
    }

    prefetchInfraredImages(contextMap, (offset) => getNextImageId(offset), 3, !!flight.demoId)

    useEffectUnsafe(() => {
        const down = (e: KeyboardEvent) => {
            if (isFormFieldFocused(e)) return

            if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") {
                incDecImage(-1)
                e.stopPropagation()
            }
            if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") {
                incDecImage(1)
                e.stopPropagation()
            }

            if (e.key === " ") dispatch(toggleImageOverlay())
            if (e.key === "b" || e.key === "B") dispatch(toggleFieldOfView())
        }
        window.addEventListener("keydown", down, { capture: true })
        return () => window.removeEventListener("keydown", down, { capture: true })
    }, [hotspot])

    return (
        <div className="h-full flex flex-col text-gray-200">
            <div className="flex justify-between items-center text-white-1000 border-b border-white-200 py-2 px-4 bg-gray-800 rounded-t-lg min-w-0 max-h-[50px] h-full">
                <h4 className="truncate flex-1 min-w-0 overflow-hidden">
                    {hotspot ? (
                        <>
                            <span>#{hotspot.label}</span>
                            <span className="text-gray-100">
                                {hotspot.status === HotspotStatus.Removed && "- Removed"}
                            </span>
                        </>
                    ) : (
                        image?.fileName
                    )}
                </h4>
                <div className="flex gap-1 shrink-0 items-center h-full min-w-fit">
                    {isImageZoomed && (
                        <div className="flex">
                            <ButtonIcon
                                className="flex items-center flex-row-reverse gap-1 text-gray-100 px-3"
                                onClick={() => {
                                    zoomRef.current?.resetTransform()
                                    setIsImageZoomed(false)
                                }}
                                Icon={Icons.FitScreen}
                                height={24}
                                width={24}
                            >
                                <h5>Reset Zoom</h5>
                            </ButtonIcon>
                            <Divider vertical className="h-[24px]" />
                        </div>
                    )}
                    <ButtonIcon
                        onClick={() => incDecImage(-1)}
                        height={24}
                        width={24}
                        Icon={Icons.ChevronLeft}
                        className="!p-0 h-fit !w-fit text-gray-100 hover:text-white-1000"
                    />
                    <ButtonIcon
                        onClick={() => incDecImage(1)}
                        height={24}
                        width={24}
                        Icon={Icons.ChevronRight}
                        className="!p-0 h-fit !w-fit text-gray-100 hover:text-white-1000"
                    />
                </div>
            </div>
            <ScrollableArea className="h-full">
                {!!image && (
                    <div className="border-b border-white-200">
                        <InfraredImage
                            context={imageMap.get(image.id)}
                            infraredImage={image}
                            onZoomLevelChange={(zoom) => setIsImageZoomed(zoom !== 1)}
                            ref={zoomRef}
                            isDemo={!!flight.demoId}
                        />
                    </div>
                )}
                <div className="flex gap-4 px-6 py-4 flex-wrap-reverse ">
                    {hotspot && <HotspotDetails flight={flight} hotspot={hotspot} />}
                    {image && <InfraredImageDetails image={image} />}
                </div>
            </ScrollableArea>
        </div>
    )
}

const HotspotDetails: FunctionComponent<{ hotspot: HotspotWithLabel; flight: InfraredFlight }> = ({
    hotspot,
    flight,
}) => {
    const polygon = processPolygon(hotspot.polygon)
    const dispatch = useAppDispatch()
    const polygons = useAppSelector(selectActiveFirePerimeterPolygons)

    const { isEditor } = useWorkspacePermissions()
    return (
        <div className="flex flex-col gap-2 flex-1">
            <span className="xs text-gray-300">HOTSPOT DETAILS</span>
            <div className="flex flex-wrap w-full gap-x-4 gap-y-6">
                {!!polygon && (
                    <HotspotCard>
                        <div>
                            <svg viewBox="0 0 24 24" className="h-6 w-6">
                                <polygon
                                    points={polygon?.svgPoints}
                                    fill="#E51A1A26"
                                    stroke="#E51A1A66"
                                    strokeWidth="0.5"
                                />
                            </svg>
                        </div>
                        <div className="flex flex-col">
                            <small className="text-gray-400">Hotspot Size</small>
                            <h5 className="text-gray-50">
                                {polygon.boundingBox.heightFeet.toFixed(1)} ft x{" "}
                                {polygon.boundingBox.widthFeet.toFixed(1)} ft (
                                {(
                                    polygon.boundingBox.heightFeet * polygon.boundingBox.widthFeet
                                ).toFixed(1)}
                                ft²)
                            </h5>
                        </div>
                    </HotspotCard>
                )}
                {!!hotspot.distanceToPerimeter && (
                    <HotspotCard>
                        <div>
                            <HotspotInPerimeterBox
                                hotspot={hotspot}
                                polygons={polygons}
                                distanceUnit={flight.perimeterDistance?.unit as DistanceUnit}
                            />
                        </div>
                        <div className="flex flex-col">
                            <small className="text-gray-400">Distance from Perimeter</small>
                            <h5 className="text-gray-50">
                                {!!hotspot.distanceToPerimeter &&
                                    !!flight.perimeterDistance?.unit &&
                                    formatDistance({
                                        value: hotspot.distanceToPerimeter,
                                        unit: flight.perimeterDistance.unit as DistanceUnit,
                                    })}
                            </h5>
                        </div>
                    </HotspotCard>
                )}

                {!!hotspot.confidence && (
                    <HotspotCard>
                        <div className="text-white-300">
                            <Icons.GraphicEQ />
                        </div>
                        <div className="flex flex-col">
                            <small className="text-gray-400">Sensitivity</small>

                            <h5 className="text-gray-50">{hotspot.confidence.toFixed(2) ?? 0.7}</h5>
                        </div>
                    </HotspotCard>
                )}
                {hotspot.imageId === "mock" && (
                    <HotspotCard>
                        <div className="text-white-300">
                            <Icons.CalendarMonth />
                        </div>
                        <div className="flex flex-col">
                            <small className="text-gray-400 flex items-center gap-1">
                                Date Placed
                            </small>
                            <h5 className="text-gray-50">
                                {new Date(hotspot.createdAt * 1000).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </h5>
                        </div>
                    </HotspotCard>
                )}

                <HotspotCard>
                    <div className="text-white-300">
                        <Icons.Distance />
                    </div>
                    <div className="flex flex-col">
                        <small className="text-gray-400 flex items-center gap-1">
                            Hotspot Coordinate{" "}
                            <CopyButton
                                className="scale-75"
                                value={`${hotspot.latitude},${hotspot.longitude}`}
                            />
                        </small>
                        <h5 className="text-gray-50">
                            {hotspot.latitude.toFixed(4)}°, {hotspot.longitude.toFixed(4)}°
                        </h5>
                    </div>
                </HotspotCard>
            </div>
            {isEditor && hotspot.status !== HotspotStatus.Removed && (
                <>
                    <Divider margin={2} />
                    <ButtonDestructive
                        LeftIcon={Icons.Trash}
                        title="Remove Hotspot"
                        onClick={() => {
                            dispatch(
                                setWorkspaceConfirmationModalContext({
                                    entityName: hotspot.label,
                                    entityId: hotspot.id,
                                    secondaryKey: hotspot.flightId,
                                    type: "delete-hotspot" as WorkspaceConfirmationModalName,
                                })
                            )
                            dispatch(showGlobalModal())
                        }}
                    />
                </>
            )}
        </div>
    )
}

const InfraredImageDetails: FunctionComponent<{ image: InfraredImageWithContext }> = ({
    image,
}) => {
    return (
        <div className="flex flex-col gap-2 flex-1">
            <span className="xs text-gray-300">FILE DETAILS</span>
            <div className="flex flex-col">
                {image.id && (
                    <div className="flex flex-col gap-1">
                        <div className="flex justify-between items-center">
                            <small className="text-gray-100 w-fit">File Name</small>
                            <small className="text-white-1000 truncate">{image.fileName}</small>
                        </div>
                        <Divider />
                    </div>
                )}

                {image.make && (
                    <div className="flex flex-col gap-1 mt-1">
                        <div className="flex justify-between items-center">
                            <small className="text-gray-100">Drone Model</small>
                            <small className="text-white-1000 break-all">{image.make}</small>
                        </div>
                        <Divider />
                    </div>
                )}

                {image.imageWidth && image.imageHeight && (
                    <div className="flex flex-col gap-1 mt-1">
                        <div className="flex justify-between">
                            <small className="text-gray-100">Dimensions</small>
                            <small className="text-white-1000 break-all">
                                {image.imageWidth} x {image.imageHeight}
                            </small>
                        </div>
                        <Divider />
                    </div>
                )}

                {image.focalLength && image.focalLength && (
                    <div className="flex flex-col gap-1 mt-1">
                        <div className="flex justify-between">
                            <small className="text-gray-100">Focal Length</small>
                            <small className="text-white-1000 break-all">
                                {image.focalLength.toFixed(1)} mm
                            </small>
                        </div>
                        <Divider />
                    </div>
                )}

                {image.takenAt && (
                    <div className="flex flex-col gap-1 mt-1">
                        <div className="flex justify-between">
                            <small className="text-gray-100">Date Created</small>
                            <small className="text-white-1000 break-all">
                                {formatDate({
                                    value: image.takenAt * 1000,
                                    format: DateFormat.MMMDDYYYY,
                                })}
                            </small>
                        </div>
                        <Divider />
                    </div>
                )}
            </div>
        </div>
    )
}

const HotspotCard = ({ children }: PropsWithChildren) => (
    <div className="flex gap-2 min-w-[200px] items-center h-fit">{children}</div>
)

const hotspotPanelId = "hotspot-panel"

const HotspotPanel = memo(() => {
    const dispatch = useAppDispatch()
    const selectedItems = useAppSelector(selectHotspotPanelItems)
    const hotspot = useAppSelector(selectHotspotWithLabelById(selectedItems.hotspotId ?? ""))

    const flightId = selectedItems.flightId || ""
    const flight = useAppSelector(selectInfraredFlightById(flightId))
    const image = useAppSelector(selectInfraredImageById(selectedItems.imageId))
    const listOpen = useAppSelector(selectHotspotPanelListOpen)
    useCursorTracking(hotspotPanelId)

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === "Escape") dispatch(closeHotspotPanel())
        }
        addEventListener("keydown", handleKeyPress)
        return () => removeEventListener("keydown", handleKeyPress)
    }, [dispatch])

    if (!flight) {
        return null
    }

    return (
        <Resizable
            className="min-w-[25vw] max-w-[50vw] ml-3 mr-2 mb-2 flex border border-white-200 rounded-lg"
            defaultSize={{ width: "40vw" }}
            enable={{ right: true }}
        >
            <div className="flex divide-x divide-white-200 w-full" id={hotspotPanelId}>
                {listOpen && <HotspotPanelList flight={flight} image={image} hotspot={hotspot} />}
                <div className="flex-1 min-w-0">
                    <HotspotPanelContent
                        image={image}
                        hotspot={hotspot}
                        flight={flight}
                        key={`${flightId}-${image?.id}-${hotspot?.id}`}
                    />
                </div>
            </div>
        </Resizable>
    )
})

HotspotPanel.displayName = "HotspotPanel"

export { HotspotPanel }
